import LogoSection from './LogoSection';
import useLogoutFn from '@/hooks/useLogoutFn';
import { useEffect } from 'react';
import { RouterLink } from '@/components/RouterLink';
import { LogIn } from 'lucide-react';
import { SidebarTrigger } from '@layout/Sidebar/Sidebar';
import HeaderActions from './HeaderActions';
import LanguageSelect from '@components/LanguageSelect';
import { Button, useTailwindBreakpoint } from '@eye-able/ui';
import { useOrgBranding } from '@/branding/useOrgBranding';
import { cn } from '@helpers/cn';
import { LOGIN_PATH } from '@/helpers/authenticationPaths';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';

interface HeaderProps {
  /**
   * For pages reachable without a session (e.g. /demo). Shows only the
   * language switcher and a sign-in link, since profile, notifications, guest
   * orgs and the sidebar toggle all need a user. Positioned statically, so the
   * page needs no header spacer.
   */
  isPublic?: boolean;
}

function Header({ isPublic = false }: HeaderProps) {
  const { t } = useInlineTranslation();
  const xlAndUp = useTailwindBreakpoint('xl');
  const logoutFn = useLogoutFn();
  const branding = useOrgBranding();

  useEffect(() => {
    // Without a session there is nothing to log out of.
    if (isPublic) return;

    function logoutOtherTabs(event: StorageEvent) {
      if (event.key === 'logout') {
        void logoutFn(true);
      }
    }

    window.addEventListener('storage', logoutOtherTabs);

    return () => {
      window.removeEventListener('storage', logoutOtherTabs);
    };
  }, [isPublic, logoutFn]);

  return (
    <header
      id='dashboard-header'
      tabIndex={-1}
      className={cn(
        'eye:bg-white eye:w-full eye:border-b eye:border-solid eye:border-grey-20 eye:outline-none',
        !isPublic && 'eye:fixed eye:top-0 eye:left-0 eye:z-1299',
      )}
      style={{
        top: 'var(--app-announcement-height, 0px)',
        paddingRight: 'var(--removed-body-scroll-bar-size, 0px)',
        // Brand colours override eye:bg-white / border / text only when
        // configured; `color` lets header icons/text inherit the brand text.
        ...(branding && {
          backgroundColor: branding.brandColor,
          borderBottomColor: branding.brandHoverColor,
          color: branding.brandTextColor,
        }),
      }}
    >
      <div className='eye:flex eye:h-[52px] eye:xl:h-[75px] eye:px-12 eye:xl:px-48 eye:items-center eye:justify-between'>
        <div className='eye:flex eye:items-center'>
          <LogoSection dark />
        </div>
        {isPublic ? (
          <div className='eye:flex eye:items-center eye:gap-3xs'>
            <LanguageSelect />
            <Button asChild variant='outline'>
              <RouterLink to={LOGIN_PATH}>
                <LogIn aria-hidden />
                {t('signIn')}
              </RouterLink>
            </Button>
          </div>
        ) : xlAndUp ? (
          <HeaderActions />
        ) : (
          <SidebarTrigger />
        )}
      </div>
    </header>
  );
}

export default Header;
